import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import PdfSigningViewer from '../../components/pdfViewer/PdfSigningViewer';
import PaginatedDocument from '../../components/app/PaginatedDocument';

const API_BASE = import.meta.env.VITE_API_URL || '';
const FONT = '"Geist", system-ui, sans-serif';
const GUEST_CHAT_LIMIT = 3;

interface GuestChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SharedDocumentResult {
  fileId: string;
  downloadUrl?: string;
  fileName: string;
  mimeType: string;
}

type SharedDraftStatus = 'idle' | 'pending' | 'streaming' | 'complete' | 'failed';

interface SharedSessionData {
  conversation: { id: string; title: string };
  messages: Array<Record<string, unknown>>;
  latestResult: SharedDocumentResult | null;
  draftPreview?: { title: string; html: string } | null;
  draftStatus?: SharedDraftStatus;
  pendingDraft?: { currentDraftHtml?: string | null; since?: string | null } | null;
  sessionSnapshot: Record<string, unknown> | null;
  permission: 'view' | 'comment' | 'edit';
  ownerName: string;
}

function docFromSnapshot(snapshot: Record<string, unknown> | null): SharedDocumentResult | null {
  if (!snapshot) return null;
  const candidates = [snapshot.currentResult, snapshot.primaryDocument, snapshot.lastConversionOutput];
  for (const raw of candidates) {
    if (!raw || typeof raw !== 'object') continue;
    const doc = raw as Record<string, unknown>;
    const fileId = typeof doc.fileId === 'string' ? doc.fileId : null;
    if (!fileId || fileId.startsWith('draft-')) continue;
    const fileName = typeof doc.fileName === 'string'
      ? doc.fileName
      : typeof doc.name === 'string'
        ? doc.name
        : 'Shared document';
    const mimeType = typeof doc.mimeType === 'string'
      ? doc.mimeType
      : fileName.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : 'application/octet-stream';
    return {
      fileId,
      downloadUrl: typeof doc.downloadUrl === 'string' ? doc.downloadUrl : undefined,
      fileName,
      mimeType,
    };
  }
  return null;
}

function GuestMarkdownMessage({ content, tone = 'light' }: { content: string; tone?: 'light' | 'dark' }) {
  const textColor = tone === 'dark' ? '#fff' : '#3D3229';
  const mutedColor = tone === 'dark' ? 'rgba(255,255,255,0.82)' : '#6B5E52';
  return (
    <div style={{ color: textColor }}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <h1 style={{ margin: '0 0 8px', fontSize: 16, lineHeight: '21px', fontWeight: 700, color: textColor, fontFamily: FONT }}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 style={{ margin: '8px 0 6px', fontSize: 14, lineHeight: '19px', fontWeight: 700, color: textColor, fontFamily: FONT }}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 style={{ margin: '8px 0 5px', fontSize: 13, lineHeight: '18px', fontWeight: 700, color: textColor, fontFamily: FONT }}>
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: '19px', color: textColor, fontFamily: FONT }}>
              {children}
            </p>
          ),
          strong: ({ children }) => <strong style={{ fontWeight: 700, color: textColor }}>{children}</strong>,
          ul: ({ children }) => (
            <ul style={{ margin: '4px 0 10px', paddingLeft: 18, color: mutedColor, fontFamily: FONT }}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '4px 0 10px', paddingLeft: 18, color: mutedColor, fontFamily: FONT }}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li style={{ margin: '0 0 6px', fontSize: 13, lineHeight: '19px', color: textColor, fontFamily: FONT }}>
              {children}
            </li>
          ),
          code: ({ children }) => (
            <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, background: tone === 'dark' ? 'rgba(255,255,255,0.14)' : '#EFE9E3', borderRadius: 4, padding: '1px 4px' }}>
              {children}
            </code>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default function SharedDocumentPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SharedSessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [guestId] = useState(() => {
    const existing = localStorage.getItem('corner.sharedGuestId');
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem('corner.sharedGuestId', next);
    return next;
  });
  const [guestMessages, setGuestMessages] = useState<GuestChatMessage[]>([]);
  const [guestInput, setGuestInput] = useState('');
  const [guestRemaining, setGuestRemaining] = useState(GUEST_CHAT_LIMIT);
  const [guestChatLoading, setGuestChatLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/sharing/link/${token}/session`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Link not found');
        }
        return res.json();
      })
      .then((session: SharedSessionData) => setData(session))
      .catch((err) => setError(err.message ?? 'Something went wrong'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/sharing/link/${token}/chat/quota`, {
      headers: { 'x-corner-guest-id': guestId },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((quota) => {
        if (!quota) return;
        setGuestRemaining(Number(quota.remaining ?? GUEST_CHAT_LIMIT));
        if (Array.isArray(quota.recentMessages)) {
          setGuestMessages(quota.recentMessages.map((m: Record<string, unknown>) => ({
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: String(m.content ?? ''),
          })).filter((m: GuestChatMessage) => m.content));
        }
      })
      .catch(() => {});
  }, [guestId, token]);

  useEffect(() => {
    if (!token || !data) return;
    const isLiveDraft = data.draftStatus === 'pending' || data.draftStatus === 'streaming';
    if (!isLiveDraft) return;

    let cancelled = false;
    const refreshSharedDraft = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/sharing/link/${token}/session`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const session = await res.json() as SharedSessionData;
        if (!cancelled) setData(session);
      } catch {
        // Keep the last good preview visible if the public poll hiccups.
      }
    };

    const interval = window.setInterval(() => {
      void refreshSharedDraft();
    }, 1_200);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [data?.draftStatus, token]);

  const handleInteraction = useCallback(() => {
    setShowSignupModal(true);
  }, []);

  const handleGuestSend = useCallback(async () => {
    const message = guestInput.trim();
    if (!message || !token || guestChatLoading) return;
    if (guestRemaining <= 0) {
      setShowSignupModal(true);
      return;
    }
    setGuestInput('');
    setGuestMessages((prev) => [...prev, { role: 'user', content: message }]);
    setGuestChatLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sharing/link/${token}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-corner-guest-id': guestId,
        },
        body: JSON.stringify({ message }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setGuestRemaining(0);
        setShowSignupModal(true);
        return;
      }
      if (!res.ok) throw new Error(body.error || 'Could not answer that question.');
      setGuestMessages((prev) => [...prev, { role: 'assistant', content: String(body.answer ?? '') }]);
      setGuestRemaining(Number(body.remaining ?? 0));
    } catch (err) {
      setGuestMessages((prev) => [...prev, {
        role: 'assistant',
        content: err instanceof Error ? err.message : 'Could not answer that question right now.',
      }]);
    } finally {
      setGuestChatLoading(false);
    }
  }, [guestChatLoading, guestId, guestInput, guestRemaining, token]);

  if (loading) {
    return (
      <div style={centerStyle}>
        <p style={{ margin: 0, fontSize: 14, color: '#9E9189', fontFamily: FONT }}>Loading shared document...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={centerStyle}>
        <div style={errorCardStyle}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" />
          </svg>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#3D3229', fontFamily: FONT }}>
            Document unavailable
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#9E9189', fontFamily: FONT, textAlign: 'center', lineHeight: '20px' }}>
            {error === 'Link not found' ? 'This shared link is no longer active or has been disabled.' : error}
          </p>
          <button onClick={() => navigate('/')} style={secondaryButtonStyle}>Go to Corner</button>
        </div>
      </div>
    );
  }

  const liveDraftActive = data.draftStatus === 'pending' || data.draftStatus === 'streaming';
  const sharedDoc = liveDraftActive
    ? null
    : data.latestResult ?? docFromSnapshot(data.sessionSnapshot);
  const fileUrl = sharedDoc
    ? `${API_BASE}/api/sharing/link/${token}/file/${sharedDoc.fileId}`
    : null;
  const isPdf = sharedDoc?.mimeType?.includes('pdf') || sharedDoc?.fileName?.toLowerCase().endsWith('.pdf');
  const isImage = sharedDoc?.mimeType?.startsWith('image/');
  const fileCanRenderInline = Boolean(fileUrl && (isPdf || isImage));
  // Generated Corner drafts have a canonical HTML body in the conversation.
  // Prefer that over generated PDF mirrors on public share pages because the
  // PDF export can flatten headings/bold text while the owner workspace still
  // renders the rich paginated HTML.
  const shouldRenderDraftPreview = Boolean(data.draftPreview?.html) && (
    liveDraftActive
    || !fileCanRenderInline
    || sharedDoc?.mimeType?.includes('pdf')
  );
  const conversationId = data.conversation.id;
  const inviteRedirect = `/app/shared/invite/${conversationId}?guestId=${encodeURIComponent(guestId)}`;

  return (
    <div style={pageStyle}>
      {/* ─── Top Bar ─── */}
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="24" height="24" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
            <defs>
              <linearGradient id="sharedLogo" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
                <stop stopColor="#D46A3A" /><stop offset="1" stopColor="#8B3A10" />
              </linearGradient>
            </defs>
            <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#sharedLogo)" strokeWidth="40" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#3D3229', fontFamily: FONT }}>Corner</span>
          <div style={{ width: 1, height: 16, background: '#E5DDD5', margin: '0 4px' }} />
          <span style={{ fontSize: 13, fontWeight: 400, color: '#9E9189', fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
            {data.conversation.title}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#B5AA9C', fontFamily: FONT }}>
            Shared by {data.ownerName}
          </span>
          <button
            onClick={() => navigate(`/auth/login?redirect=${encodeURIComponent(inviteRedirect)}`)}
            style={loginButtonStyle}
          >
            Log in
          </button>
          <button
            onClick={() => navigate(`/auth/register?redirect=${encodeURIComponent(inviteRedirect)}`)}
            style={signupButtonStyle}
          >
            Sign up free
          </button>
        </div>
      </div>

      {/* ─── Document Area ─── */}
      <div style={contentAreaStyle}>
        {shouldRenderDraftPreview ? (
          <div style={{ ...docContainerStyle, ...paginatedDocContainerStyle, cursor: 'default' }}>
            {liveDraftActive && (
              <div style={liveDraftStatusStyle}>
                <span style={liveDraftPulseStyle} />
                Corner is still writing
              </div>
            )}
            <PaginatedDocument
              html={data.draftPreview?.html ?? ''}
              streaming={liveDraftActive}
              zoom={0.92}
              id="corner-shared-paginated-doc"
            />
          </div>
        ) : fileUrl && isPdf ? (
          <div style={{ ...docContainerStyle, overflow: 'hidden' }}>
            <PdfSigningViewer
              pdfUrl={fileUrl}
              documentId={sharedDoc?.fileId}
              mode="sign"
              fields={[]}
              showThumbnails
            />
          </div>
        ) : fileUrl && isImage ? (
          <div style={docContainerStyle} onClick={handleInteraction}>
            <img src={fileUrl} alt={sharedDoc?.fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        ) : (
          <div style={{ ...docContainerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleInteraction}>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#C5BFB8" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 16 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6" />
              </svg>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: '#3D3229', fontFamily: FONT }}>
                {data.conversation.title}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9E9189', fontFamily: FONT }}>
                {liveDraftActive
                  ? 'Corner is still writing this document. The live preview will appear here as soon as the first draft content is ready.'
                  : 'No preview is available for this link yet. Sign in with your invite to open the full workspace, or ask the owner to share again once the document is ready.'}
              </p>
            </div>
          </div>
        )}

        <div style={chatPreviewStyle}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#9E9189', textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 12, fontFamily: FONT }}>
            Ask Corner AI
          </div>
          <div style={guestChatBoxStyle}>
            <div style={{ fontSize: 13, color: '#6B5E52', fontFamily: FONT, lineHeight: '18px' }}>
              Ask up to {GUEST_CHAT_LIMIT} questions about this shared document before creating an account.
            </div>
            <div style={{ fontSize: 12, color: guestRemaining > 0 ? '#9E9189' : '#D97645', fontFamily: FONT }}>
              {guestRemaining > 0 ? `${guestRemaining} free chat${guestRemaining === 1 ? '' : 's'} left` : 'Sign up to keep chatting'}
            </div>
            <div style={guestMessagesStyle}>
              {guestMessages.length === 0 ? (
                <div style={{ fontSize: 13, color: '#B5AA9C', fontFamily: FONT, lineHeight: '18px' }}>
                  Try “Summarize this document” or “What should I pay attention to?”
                </div>
              ) : guestMessages.map((msg, i) => (
                <div key={i} style={{
                  ...guestBubbleStyle,
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  background: msg.role === 'user' ? '#1A1714' : '#F7F4F0',
                  color: msg.role === 'user' ? '#fff' : '#3D3229',
                }}>
                  <GuestMarkdownMessage content={msg.content} tone={msg.role === 'user' ? 'dark' : 'light'} />
                </div>
              ))}
              {guestChatLoading && (
                <div style={{ ...guestBubbleStyle, alignSelf: 'flex-start', background: '#F7F4F0', color: '#9E9189' }}>
                  Thinking...
                </div>
              )}
            </div>
            <div style={guestInputRowStyle}>
              <input
                value={guestInput}
                onChange={(e) => setGuestInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleGuestSend();
                  }
                }}
                placeholder={guestRemaining > 0 ? 'Ask about this document...' : 'Sign up to keep chatting'}
                disabled={guestRemaining <= 0 || guestChatLoading}
                style={guestInputStyle}
              />
              <button
                onClick={() => void handleGuestSend()}
                disabled={!guestInput.trim() || guestRemaining <= 0 || guestChatLoading}
                style={{
                  ...guestSendButtonStyle,
                  opacity: !guestInput.trim() || guestRemaining <= 0 || guestChatLoading ? 0.45 : 1,
                  cursor: !guestInput.trim() || guestRemaining <= 0 || guestChatLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Send
              </button>
            </div>
            {guestRemaining <= 0 && (
              <button onClick={() => setShowSignupModal(true)} style={guestSignupInlineStyle}>
                Sign up free to continue
              </button>
            )}
          </div>

          {data.messages.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#9E9189', textTransform: 'uppercase' as const, letterSpacing: '0.05em', margin: '18px 0 12px', fontFamily: FONT }}>
                Shared Conversation
              </div>
            {data.messages.slice(-8).map((msg, i) => {
              const role = String(msg.role ?? 'user');
              const content = String(msg.content ?? msg.text ?? '');
              if (!content) return null;
              return (
                <div key={i} style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: role === 'assistant' ? '#F7F4F0' : 'transparent' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#B5AA9C', marginBottom: 2, fontFamily: FONT }}>
                    {role === 'assistant' ? 'Corner AI' : data.ownerName}
                  </div>
                  <div style={{ fontSize: 13, color: '#3D3229', lineHeight: '18px', fontFamily: FONT, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const }}>
                    <GuestMarkdownMessage content={content.length > 300 ? content.slice(0, 300) + '...' : content} />
                  </div>
                </div>
              );
            })}
            </>
          )}
        </div>
      </div>

      {/* ─── Guest Banner ─── */}
      <div style={bannerStyle}>
        <span style={{ fontSize: 13, color: '#6B5E52', fontFamily: FONT }}>
          You're viewing a shared document. You can ask {GUEST_CHAT_LIMIT} free AI questions as a guest.
        </span>
        <button onClick={handleInteraction} style={bannerCtaStyle}>
          Sign up for full access
        </button>
      </div>

      {/* ─── Signup Modal ─── */}
      {showSignupModal && (
        <div style={overlayStyle} onClick={() => setShowSignupModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <svg width="32" height="32" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0, marginBottom: 8 }}>
              <defs>
                <linearGradient id="modalLogo" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#D46A3A" /><stop offset="1" stopColor="#8B3A10" />
                </linearGradient>
              </defs>
              <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#modalLogo)" strokeWidth="40" strokeLinecap="round" />
            </svg>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#3D3229', fontFamily: FONT }}>
              Sign up to interact
            </h2>
            <p style={{ margin: '6px 0 20px', fontSize: 14, color: '#9E9189', fontFamily: FONT, textAlign: 'center', lineHeight: '20px' }}>
              Create a free Corner account to edit, comment, and collaborate on this document.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              <button
                onClick={() => navigate(`/auth/register?redirect=${encodeURIComponent(inviteRedirect)}`)}
                style={signupButtonLargeStyle}
              >
                Sign up free
              </button>
              <button
                onClick={() => navigate(`/auth/login?redirect=${encodeURIComponent(inviteRedirect)}`)}
                style={loginButtonLargeStyle}
              >
                I already have an account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────

const centerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: '100vh', width: '100vw', background: '#F5F0EB', fontFamily: FONT,
};

const errorCardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
  padding: 40, background: '#fff', borderRadius: 16,
  border: '1px solid #E5DDD5', maxWidth: 400,
};

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw',
  background: '#F5F0EB', fontFamily: FONT,
};

const topBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 20px', height: 52, borderBottom: '1px solid #E5DDD5',
  background: '#fff', flexShrink: 0,
};

const contentAreaStyle: React.CSSProperties = {
  flex: 1, display: 'flex', overflow: 'hidden', gap: 0,
};

const docContainerStyle: React.CSSProperties = {
  flex: 1, overflow: 'auto', cursor: 'pointer',
  background: '#EDEDED', position: 'relative',
};

const paginatedDocContainerStyle: React.CSSProperties = {
  background: '#EEEAE4',
  padding: '28px 20px 42px',
};

const liveDraftStatusStyle: React.CSSProperties = {
  position: 'sticky',
  top: 14,
  zIndex: 5,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  margin: '0 0 14px 12px',
  padding: '8px 11px',
  borderRadius: 999,
  border: '1px solid rgba(159, 91, 52, 0.22)',
  background: 'rgba(255, 253, 250, 0.92)',
  color: '#7A4627',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: FONT,
  boxShadow: '0 8px 24px rgba(55, 39, 24, 0.08)',
};

const liveDraftPulseStyle: React.CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: 999,
  background: '#2F9E66',
  boxShadow: '0 0 0 4px rgba(47, 158, 102, 0.12)',
  flexShrink: 0,
};

const chatPreviewStyle: React.CSSProperties = {
  width: 380, borderLeft: '1px solid #E5DDD5', background: '#fff',
  padding: 20, overflowY: 'auto', flexShrink: 0,
};

const guestChatBoxStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 10,
  padding: 12, border: '1px solid #E5DDD5', borderRadius: 12,
  background: '#FFFDFB',
  minHeight: 520,
};

const guestMessagesStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8,
  minHeight: 330, maxHeight: 'min(56vh, 560px)', overflowY: 'auto',
  padding: '4px 0',
};

const guestBubbleStyle: React.CSSProperties = {
  maxWidth: '94%', borderRadius: 10, padding: '10px 12px',
  fontSize: 13, lineHeight: '18px', fontFamily: FONT,
  wordBreak: 'break-word',
};

const guestInputRowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center',
};

const guestInputStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, border: '1px solid #E5DDD5',
  borderRadius: 9, padding: '9px 10px',
  fontSize: 13, color: '#3D3229', fontFamily: FONT,
  outline: 'none', background: '#fff',
};

const guestSendButtonStyle: React.CSSProperties = {
  background: '#1A1714', border: 'none', borderRadius: 9,
  padding: '9px 12px', fontSize: 13, fontWeight: 600,
  color: '#fff', fontFamily: FONT,
};

const guestSignupInlineStyle: React.CSSProperties = {
  background: '#D97645', border: 'none', borderRadius: 9,
  padding: '10px 12px', fontSize: 13, fontWeight: 600,
  color: '#fff', fontFamily: FONT, cursor: 'pointer',
};

const bannerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '10px 20px', background: '#FFF8F3', borderTop: '1px solid #F0DFD0',
  flexShrink: 0,
};

const bannerCtaStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 13, fontWeight: 600, color: '#D97645', fontFamily: FONT,
  textDecoration: 'underline', padding: 0,
};

const loginButtonStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #E5DDD5', borderRadius: 8, cursor: 'pointer',
  padding: '6px 14px', fontSize: 13, fontWeight: 500, color: '#3D3229', fontFamily: FONT,
};

const signupButtonStyle: React.CSSProperties = {
  background: '#1A1714', border: 'none', borderRadius: 8, cursor: 'pointer',
  padding: '6px 14px', fontSize: 13, fontWeight: 500, color: '#fff', fontFamily: FONT,
};

const secondaryButtonStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #E5DDD5', borderRadius: 8, cursor: 'pointer',
  padding: '8px 20px', fontSize: 14, fontWeight: 500, color: '#3D3229', fontFamily: FONT,
  marginTop: 8,
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};

const modalStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  background: '#fff', borderRadius: 16, padding: '32px 36px',
  maxWidth: 380, width: '90%', boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
};

const signupButtonLargeStyle: React.CSSProperties = {
  background: '#1A1714', border: 'none', borderRadius: 10, cursor: 'pointer',
  padding: '12px 0', fontSize: 15, fontWeight: 600, color: '#fff', fontFamily: FONT,
  width: '100%',
};

const loginButtonLargeStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #E5DDD5', borderRadius: 10, cursor: 'pointer',
  padding: '12px 0', fontSize: 15, fontWeight: 500, color: '#3D3229', fontFamily: FONT,
  width: '100%',
};
